import React, { useState, useEffect, useCallback } from "react";
import { Table, Spinner } from "react-bootstrap";
import { RiCloseCircleLine } from "react-icons/ri";
import { MdFilterAlt, MdFilterAltOff } from "react-icons/md";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL;

const NoDataMessage = ({ message = "Sorry, no data found" }) => (
  <div className="text-center py-4 text-muted">
    <div className="no-data-icon mb-2">
      <RiCloseCircleLine size={40} className="text-secondary" />
    </div>
    <p className="mb-0">{message}</p>
  </div>
);

const SelfGiftHistory = () => {
  const navigate = useNavigate();
  const [giftList, setGiftList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [filteredGifts, setFilteredGifts] = useState([]);
  const [processedGifts, setProcessedGifts] = useState([]);
  const [eligibilityData, setEligibilityData] = useState([]);
  const [isFilterActive, setIsFilterActive] = useState(false);

  const handleToggle = () => setIsFilterActive(!isFilterActive);

  const getAuthToken = () => localStorage.getItem("token");

  const formatNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number)
      ? Number.isInteger(number)
        ? number
        : number.toFixed(2)
      : 0;
  };

  const getNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const normalizeOfferName = (value) =>
    String(value || "").trim().toLowerCase().replace(/\s+/g, " ");

  const parseGiftDate = (dateValue) => {
    if (!dateValue) return null;
    try {
      const value = String(dateValue).trim();
      if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
        const [day, month, year] = value.split("-").map(Number);
        return new Date(year, month - 1, day);
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split("-").map(Number);
        return new Date(year, month - 1, day);
      }
      const parsed = new Date(value);
      if (!isNaN(parsed.getTime())) {
        return new Date(
          parsed.getFullYear(),
          parsed.getMonth(),
          parsed.getDate()
        );
      }
      return null;
    } catch {
      return null;
    }
  };

  const isDateInRange = (leadDate, fromDate, toDate) => {
    const lead = parseGiftDate(leadDate);
    const from = parseGiftDate(fromDate);
    const to = parseGiftDate(toDate);
    if (!lead || !from || !to) return false;
    return lead >= from && lead <= to;
  };

  const getProjectIds = (value) => {
    if (Array.isArray(value)) {
      return value
        .flatMap((item) =>
          String(item ?? "").split(",").map((id) => id.trim())
        )
        .filter(Boolean);
    }
    if (value !== null && value !== undefined && value !== "") {
      return String(value).split(",").map((id) => id.trim()).filter(Boolean);
    }
    return [];
  };

  const isUnitGift = (gift) => {
    if (gift?.calculation_type === "unit") return true;
    if (Number(gift?.unit || 0) > 0) return true;
    if (Number(gift?.required_unit || 0) > 0) return true;
    return false;
  };

  const isSmallUnitGift = (gift) => {
    if (gift?.calculation_type === "smallunit") return true;
    if (Number(gift?.smallunit || 0) > 0) return true;
    if (Number(gift?.required_smallunit || 0) > 0) return true;
    return false;
  };

  const getGiftRequirement = (gift) => {
    if (isUnitGift(gift)) {
      return getNumber(gift?.required_unit || gift?.unit || 0);
    }
    if (isSmallUnitGift(gift)) {
      return getNumber(gift?.required_smallunit || gift?.smallunit || 0);
    }
    return getNumber(gift?.required_area_sqyd || gift?.area_sqyd || 0);
  };

  const fetchProjects = async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(
        `https://api.shubhbhoomirealestate.com/frontapi/project-list-block`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (
        data.status === "1" &&
        Array.isArray(data.data) &&
        data.data.length > 0
      ) {
        setProjects(data.data);
      } else {
        setProjects([]);
      }
    } catch (error) {
      console.error("Projects fetch error:", error);
      setProjects([]);
    }
  };

  const fetchGiftList = useCallback(async (projectId = "") => {
    try {
      setLoading(true);
      setError(null);
      const token = getAuthToken();

      const url = projectId
        ? `${API_URL}/self-gift-list?project_id=${encodeURIComponent(
            projectId
          )}`
        : `${API_URL}/self-gift-list`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (
        data.success === "1" &&
        Array.isArray(data.data) &&
        data.data.length > 0
      ) {
        const sortedGifts = [...data.data].sort(
          (a, b) => parseFloat(a.area_sqyd) - parseFloat(b.area_sqyd)
        );
        setGiftList(sortedGifts);
      } else {
        setGiftList([]);
      }
    } catch (error) {
      console.error("Gift list fetch error:", error);
      setError("Failed to load gift history");
      setGiftList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEligibilityData = async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/assiciate-gift-eligibility`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      const result = await response.json();
      if (result?.success && Array.isArray(result.data)) {
        return result.data;
      }
      return [];
    } catch (error) {
      console.error("Error fetching eligibility:", error);
      return [];
    }
  };

  const buildEligibleGiftMap = (eligibility) => {
    const map = new Map();

    (eligibility || []).forEach((project) => {
      if (Array.isArray(project?.gifts)) {
        project.gifts.forEach((g) => {
          if (g?.status !== "Eligible") return;

          const giftId =
            g?.id !== null && g?.id !== undefined
              ? String(g.id)
              : [
                  normalizeOfferName(g?.offer_name || g?.gift_name),
                  g?.required_unit || 0,
                  g?.required_area_sqyd || 0,
                ].join("-");

          if (!map.has(giftId)) {
            map.set(giftId, {
              ...g,
              project_id: g?.project_id ?? String(project?.project_id ?? ""),
              project_name: g?.project_name || project?.project_name || "-",
              qualifying_leads: Array.isArray(g?.qualifying_leads)
                ? g.qualifying_leads
                : [],
            });
          }
        });
      }
    });

    return Array.from(map.values());
  };

  const findEligibleGift = (gift, eligibleGifts) => {
    if (!Array.isArray(eligibleGifts) || eligibleGifts.length === 0)
      return null;

    const selfOfferName = normalizeOfferName(
      gift?.offer_name || gift?.gift_name
    );
    if (!selfOfferName) return null;

    const selfProjectIds = getProjectIds(
      gift?.project_ids?.length ? gift.project_ids : gift?.project_id
    );

    for (const eligibleGift of eligibleGifts) {
      const eligibleOfferName = normalizeOfferName(
        eligibleGift?.offer_name || eligibleGift?.gift_name
      );
      if (!eligibleOfferName || selfOfferName !== eligibleOfferName) continue;

      const eligibleProjectId =
        eligibleGift?.project_id !== null &&
        eligibleGift?.project_id !== undefined
          ? String(eligibleGift.project_id).trim()
          : "";

      if (
        selfProjectIds.length > 0 &&
        eligibleProjectId &&
        !selfProjectIds.includes(eligibleProjectId)
      ) {
        const qualifyingProjectIds = getProjectIds(
          eligibleGift?.qualifying_project_ids
        );
        const projectMatched = qualifyingProjectIds.some((id) =>
          selfProjectIds.includes(String(id))
        );
        if (!projectMatched) continue;
      }

      const giftDateFrom =
        gift?.date_from || gift?.gift_date_from || eligibleGift?.gift_date_from;
      const giftDateTo =
        gift?.date_to || gift?.gift_date_to || eligibleGift?.gift_date_to;
      if (!giftDateFrom || !giftDateTo) continue;

      const qualifyingLeads = Array.isArray(eligibleGift?.qualifying_leads)
        ? eligibleGift.qualifying_leads
        : [];

      const matchingLead = qualifyingLeads.find((lead) => {
        const leadDate =
          lead?.lead_date_string ||
          lead?.lead_date ||
          lead?.parsed_lead_date ||
          "";
        return isDateInRange(leadDate, giftDateFrom, giftDateTo);
      });

      if (!matchingLead && !eligibleGift?.lead_date) continue;

      return {
        ...eligibleGift,
        project_id: eligibleProjectId || eligibleGift?.project_id || "",
        project_name: eligibleGift?.project_name || "-",
        lead_date:
          matchingLead?.lead_date_string ||
          matchingLead?.lead_date ||
          matchingLead?.parsed_lead_date ||
          eligibleGift?.lead_date ||
          "",
      };
    }

    return null;
  };

  const getGiftProjectName = (gift, eligibleGift, eligibility) => {
    if (eligibleGift?.project_name) return eligibleGift.project_name;

    const giftProjectIds = getProjectIds(
      gift?.project_ids?.length ? gift.project_ids : gift?.project_id
    );

    const matchedProject = (eligibility || []).find((project) => {
      const projectId =
        project?.project_id !== null && project?.project_id !== undefined
          ? String(project.project_id).trim()
          : "";
      return projectId && giftProjectIds.includes(projectId);
    });

    if (matchedProject?.project_name) return matchedProject.project_name;
    return gift?.project_name || gift?.offer_project_name || "-";
  };

  const handleFilter = async () => {
    await fetchGiftList(selectedProject);
  };

  const handleReset = async () => {
    setSelectedProject("");
    await fetchGiftList("");
  };

  useEffect(() => {
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const token = localStorage.getItem("token");

    if (isLoggedIn !== "true" || !token) {
      navigate("/login");
      return;
    }

    (async () => {
      await Promise.all([fetchGiftList(), fetchProjects()]);
    })();
  }, [navigate, fetchGiftList]);

  useEffect(() => {
    let isCancelled = false;

    const processGifts = async () => {
      const displayGiftList = giftList.filter(
        (gift) => String(gift?.gift_type || "").toLowerCase() === "self"
      );

      if (displayGiftList.length === 0) {
        setProcessedGifts([]);
        setFilteredGifts([]);
        return;
      }

      const eligibility = await fetchEligibilityData();
      if (isCancelled) return;
      setEligibilityData(eligibility);

      const eligibleGifts = buildEligibleGiftMap(eligibility);

      const processed = displayGiftList.map((gift) => {
        const eligibleGift = findEligibleGift(gift, eligibleGifts);
        const isEligible = !!eligibleGift;
        const unitGift = isUnitGift(gift);
        const giftRequirement = getGiftRequirement(gift);

        const requirementLabel = unitGift
          ? `${formatNumber(giftRequirement)} Units`
          : `${formatNumber(giftRequirement)} SQYD`;

        const achievedValue = isEligible
          ? unitGift
            ? getNumber(eligibleGift?.achieved_unit)
            : getNumber(eligibleGift?.achieved_area_sqyd)
          : 0;

        const projectName = getGiftProjectName(gift, eligibleGift, eligibility);

        return {
          id: gift?.id || gift?.offer_name || Math.random().toString(36),
          giftName: gift?.offer_name || gift?.gift_name || "-",
          giftItem: gift?.offer_item || gift?.gift_item || "-",
          requirement: requirementLabel,
          achieved: isEligible
            ? `${formatNumber(achievedValue)} ${unitGift ? "Units" : "SQYD"}`
            : "0",
          startDate:
            gift?.date_from ||
            gift?.gift_date_from ||
            eligibleGift?.gift_date_from ||
            "-",
          endDate:
            gift?.date_to ||
            gift?.gift_date_to ||
            eligibleGift?.gift_date_to ||
            "-",
          closingDays:
            gift?.closing_days ?? eligibleGift?.closing_days ?? "N/A",
          projectName,
          status: isEligible ? "✅ Eligible" : "⏳ Pending",
          statusColor: isEligible ? "#198754" : "#6c757d",
          isEligible,
          leadDate: eligibleGift?.lead_date || "",
        };
      });

      if (!isCancelled) {
        setProcessedGifts(processed);
        setFilteredGifts(processed);
      }
    };

    processGifts();

    return () => {
      isCancelled = true;
    };
  }, [giftList]);

  // ═══════════ INLINE STYLES ═══════════
  const gradientBg =
    "linear-gradient(135deg, #1e1b4b 0%, #3b0f6b 50%, #4c1d95 100%)";

  const styles = {
    filterToggle: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "42px",
      height: "42px",
      padding: 0,
      border: isFilterActive
        ? "1px solid #ffffff"
        : "1px solid rgba(255, 255, 255, 0.3)",
      background: isFilterActive ? "#ffffff" : "rgba(255, 255, 255, 0.15)",
      color: isFilterActive ? "#1e1b4b" : "#ffffff",
      borderRadius: "10px",
      fontSize: "18px",
      cursor: "pointer",
      transition: "all 0.25s ease",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      backdropFilter: "blur(6px)",
    },
    filterSelect: {
      flex: "1 1 0",
      minWidth: 0,
      height: "44px",
      padding: "0 40px 0 16px",
      fontSize: "13.5px",
      border: "1.5px solid #e2e8f0",
      borderRadius: "10px",
      background: "#fff",
      color: "#0f172a",
      outline: "none",
      boxSizing: "border-box",
      margin: 0,
      cursor: "pointer",
      appearance: "none",
      backgroundImage:
        "url(\"data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2364748b%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E\")",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 14px center",
      backgroundSize: "14px",
    },
    filterBtn: {
      flexShrink: 0,
      height: "44px",
      padding: "0 28px",
      fontSize: "13.5px",
      fontWeight: 700,
      color: "#fff",
      background: gradientBg,
      border: "none",
      borderRadius: "10px",
      cursor: "pointer",
      whiteSpace: "nowrap",
      boxShadow: "0 4px 14px rgba(76, 29, 149, 0.35)",
      transition: "all 0.22s ease",
      margin: 0,
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
    },
  };

  if (loading && giftList.length === 0) {
    return (
      <div className="container mt-4">
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading gift history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <NoDataMessage message={error} />
      </div>
    );
  }

  return (
    <div className="padding_15">
      <div className="card shadow-sm">
        <div className="card-header flex-wrap-mobile d-flex justify-content-between align-items-md-center gap-2">
          <div className="titlepage w-100">
            <h3 className="mb-0">Self Gift History</h3>
          </div>

          {/* ═══════════ FILTER TOGGLE ═══════════ */}
          <button
            onClick={handleToggle}
            title={isFilterActive ? "Hide Filters" : "Show Filters"}
            style={styles.filterToggle}
            onMouseEnter={(e) => {
              if (!isFilterActive) {
                e.currentTarget.style.background =
                  "rgba(255, 255, 255, 0.25)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }
            }}
            onMouseLeave={(e) => {
              if (!isFilterActive) {
                e.currentTarget.style.background =
                  "rgba(255, 255, 255, 0.15)";
                e.currentTarget.style.transform = "translateY(0)";
              }
            }}
          >
            {isFilterActive ? <MdFilterAltOff /> : <MdFilterAlt />}
          </button>
        </div>

        <div className="card-body shadow-none">
          {/* ═══════════ FILTER SECTION ═══════════ */}
          {isFilterActive && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleFilter();
              }}
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
                alignItems: "center",
                marginBottom: "20px",
                width: "100%",
              }}
            >
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                style={styles.filterSelect}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = "#6366f1";
                  e.currentTarget.style.boxShadow =
                    "0 0 0 4px rgba(99, 102, 241, 0.12)";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = "#e2e8f0";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <option value="">Select Project</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>

              <button
                type="submit"
                style={styles.filterBtn}
                disabled={loading}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 20px rgba(76, 29, 149, 0.5)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 14px rgba(76, 29, 149, 0.35)";
                }}
              >
                {loading ? "Loading..." : "Filter"}
              </button>
            </form>
          )}

          {filteredGifts.length === 0 ? (
            <NoDataMessage message="Sorry, no self gift history found" />
          ) : (
            <Table bordered hover responsive>
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Gift Name</th>
                  <th>Gift Item</th>
                  <th>Requirement</th>
                  <th>Achieved</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Closing Days</th>
                  <th>Project</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredGifts.map((gift, index) => (
                  <tr key={gift.id || index}>
                    <td>{index + 1}</td>
                    <td>
                      <strong
                        style={
                          gift.isEligible
                            ? { color: "#198754", fontWeight: "700" }
                            : {}
                        }
                      >
                        {gift.giftName}
                      </strong>
                    </td>
                    <td>{gift.giftItem}</td>
                    <td>{gift.requirement}</td>
                    <td>{gift.achieved}</td>
                    <td className="text-danger">{gift.startDate}</td>
                    <td className="text-success">{gift.endDate}</td>
                    <td>{gift.closingDays}</td>
                    <td>{gift.projectName}</td>
                    <td>
                      <span
                        style={{
                          color: gift.statusColor,
                          fontWeight: "bold",
                        }}
                      >
                        {gift.status}
                      </span>
                      {gift.isEligible && gift.leadDate && (
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#198754",
                            fontWeight: "600",
                          }}
                        >
                          Lead: {gift.leadDate}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelfGiftHistory;